import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MutedSpeakerIcon, PlusIcon, UnmutedSpeakerIcon } from '@streamr/tv-ui';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { OgrafService } from '../services/ograf/OgrafService';
import { StoredMediaService } from '../services/storedmedia/StoredMediaService';
import { StreamrAppService } from '../services/streamrapp/StreamrAppService';
import { InvitePopup } from './InvitePopup';
import { OgrafTrackCard } from './ograf/OgrafTrackCard';
import { showCameraInvitePopup } from './popups/CameraInvitePopup';
import { selectAddCamera } from './popups/devices/AddCameraSelectorPopup';
import { selectAddMicrophone } from './popups/devices/AddMicrophoneSelectorPopup';
const DEFAULT_CARD_WIDTH = 160;
const DEFAULT_CARD_HEIGHT = 90;
const CARD_GAP = 8;
function VerticalSlider({ value, onChange, }) {
    const trackRef = useRef(null);
    const updateValueFromClientY = useCallback((clientY) => {
        const el = trackRef.current;
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const clamped = Math.max(0, Math.min(relativeY, rect.height));
        const next = Math.round((1 - clamped / rect.height) * 100);
        onChange(next);
    }, [onChange]);
    const handlePointerDown = (e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        updateValueFromClientY(e.clientY);
    };
    const handlePointerMove = (e) => {
        if (e.pressure === 0 && e.buttons === 0)
            return;
        updateValueFromClientY(e.clientY);
    };
    const pct = Math.max(0, Math.min(100, value));
    return (_jsxs(VSliderTrack, { ref: trackRef, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, role: "slider", "aria-label": "Track volume", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": pct, tabIndex: 0, children: [_jsx(VSliderFill, { style: { height: `${pct}%` } }), _jsx(VSliderThumb, { style: { bottom: `${pct}%` } })] }));
}
const THUMBNAIL_WIDTH = 320;
const THUMBNAIL_HEIGHT = 180;
function ThumbnailCanvas({ trackId }) {
    const canvasRef = useRef(null);
    const thumbnailCanvasKeys = StreamrAppService.getOutput().useThumbnailCanvasKeys();
    const canvasKey = thumbnailCanvasKeys[trackId] ?? 0;
    // biome-ignore lint/correctness/useExhaustiveDependencies: canvasKey triggers re-run after canvas re-key
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        StreamrAppService.getNotificationInterface().notifyThumbnailCanvasMounted(trackId, canvas);
        return () => {
            StreamrAppService.getNotificationInterface().notifyThumbnailCanvasUnmounted(trackId);
        };
    }, [trackId, canvasKey]);
    return (_jsx(ThumbnailContainer, { children: _jsx(ThumbnailCanvasEl, { ref: canvasRef, width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT }, `thumb-${trackId}-${canvasKey}`) }));
}
function useOverflowDetection(scrollRef) {
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const updateOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const tolerance = 1;
        setCanScrollLeft(el.scrollLeft > tolerance);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
    }, [scrollRef]);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        updateOverflow();
        el.addEventListener('scroll', updateOverflow, { passive: true });
        const ro = new ResizeObserver(updateOverflow);
        ro.observe(el);
        const mo = new MutationObserver(updateOverflow);
        mo.observe(el, { childList: true, subtree: true });
        return () => {
            el.removeEventListener('scroll', updateOverflow);
            ro.disconnect();
            mo.disconnect();
        };
    }, [scrollRef, updateOverflow]);
    return { canScrollLeft, canScrollRight };
}
export function FilmStrip({ onParticipantClick, highlightedSourceId, maxVisibleItems, cardWidth = DEFAULT_CARD_WIDTH, cardHeight = DEFAULT_CARD_HEIGHT, }) {
    const filmStripGroups = StreamrAppService.getOutput().useFilmStripGroups();
    const uploadedImages = StoredMediaService.getOutput().useImages();
    const ografGraphics = OgrafService.getOutput().useGraphics();
    const [showInvitePopup, setShowInvitePopup] = useState(false);
    const inviteCardRef = useRef(null);
    const popupRef = useRef(null);
    const fileInputRef = useRef(null);
    const ografFileInputRef = useRef(null);
    const scrollRef = useRef(null);
    const [popupPos, setPopupPos] = useState(null);
    const videoTrackIds = useMemo(() => {
        const ids = [];
        for (const group of filmStripGroups) {
            for (const track of group.tracks) {
                if (track.type === 'video') {
                    ids.push(track.trackId);
                }
            }
        }
        return ids;
    }, [filmStripGroups]);
    useEffect(() => {
        StreamrAppService.getNotificationInterface().notifySetActiveThumbnailParticipantIds(videoTrackIds);
    }, [videoTrackIds]);
    useEffect(() => {
        return () => {
            StreamrAppService.getNotificationInterface().notifySetActiveThumbnailParticipantIds([]);
        };
    }, []);
    const { canScrollLeft, canScrollRight } = useOverflowDetection(scrollRef);
    const scrollBy = useCallback((direction) => {
        const el = scrollRef.current;
        if (!el)
            return;
        el.scrollBy({
            left: direction * (cardWidth + CARD_GAP),
            behavior: 'smooth',
        });
    }, [cardWidth]);
    const maxWidth = maxVisibleItems !== undefined
        ? maxVisibleItems * cardWidth + (maxVisibleItems - 1) * CARD_GAP + 2
        : undefined;
    useLayoutEffect(() => {
        if (!showInvitePopup || !inviteCardRef.current)
            return;
        const rect = inviteCardRef.current.getBoundingClientRect();
        setPopupPos({
            left: rect.left + rect.width / 2,
            top: rect.top,
        });
    }, [showInvitePopup]);
    useEffect(() => {
        if (!showInvitePopup)
            return;
        const handleClickOutside = (e) => {
            const target = e.target;
            if (inviteCardRef.current?.contains(target) ||
                popupRef.current?.contains(target)) {
                return;
            }
            setShowInvitePopup(false);
        };
        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showInvitePopup]);
    const handleUploadImage = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleShareScreen = useCallback(() => {
        StreamrAppService.getNotificationInterface().notifyShareScreen();
    }, []);
    const handleAddCamera = useCallback(() => {
        setShowInvitePopup(false);
        selectAddCamera({
            anchorRef: inviteCardRef,
            variant: 'top-center',
        });
    }, []);
    const handleAddMicrophone = useCallback(() => {
        setShowInvitePopup(false);
        selectAddMicrophone({
            anchorRef: inviteCardRef,
            variant: 'top-center',
        });
    }, []);
    const handleAddRemoteCamera = useCallback(() => {
        setShowInvitePopup(false);
        showCameraInvitePopup({
            anchorRef: inviteCardRef,
        });
    }, []);
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            StoredMediaService.getNotificationInterface().notifyAddImage(file);
        }
        e.target.value = '';
    }, []);
    const handleAddOgrafGraphic = useCallback(() => {
        ografFileInputRef.current?.click();
    }, []);
    const handleOgrafFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            OgrafService.getNotificationInterface().notifyAddOgrafGraphic(file);
        }
        e.target.value = '';
    }, []);
    const audioCardWidth = Math.round(cardWidth / 4);
    return (_jsxs(FilmStripRoot, { children: [_jsx(HiddenFileInput, { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleFileChange }), _jsx(HiddenFileInput, { ref: ografFileInputRef, type: "file", accept: ".zip", onChange: handleOgrafFileChange }), canScrollLeft && (_jsx(ArrowButton, { "$side": "left", onClick: () => scrollBy(-1), children: _jsx(ChevronLeftIcon, {}) })), _jsxs(FilmStripScroll, { ref: scrollRef, style: maxWidth ? { maxWidth } : undefined, children: [filmStripGroups.map((group, groupIndex) => (_jsxs(GroupContainer, { children: [groupIndex > 0 && (_jsx(ParticipantSeparator, { "$cardHeight": cardHeight })), group.tracks.map((track) => {
                                if (track.type === 'video') {
                                    const vt = track;
                                    return (_jsxs(VideoTrackCard, { "$highlighted": vt.trackId === highlightedSourceId, "$cardWidth": cardWidth, onClick: () => onParticipantClick?.({
                                            id: vt.trackId,
                                            name: vt.name,
                                            isYou: vt.isYou,
                                        }), children: [vt.isLocal && (_jsx(RemoveTrackButton, { "data-remove-btn": true, onClick: (e) => {
                                                    e.stopPropagation();
                                                    StreamrAppService.getNotificationInterface().notifyRemoveLocalTrack(vt.trackId);
                                                }, children: "\u2715" })), _jsx(ThumbnailArea, { "$highlighted": vt.trackId ===
                                                    highlightedSourceId, "$cardHeight": cardHeight, children: _jsx(ThumbnailCanvas, { trackId: vt.trackId }) }), _jsxs(NameRow, { children: [_jsx(PersonIconSvg, {}), _jsx(ParticipantName, { children: vt.isYou
                                                            ? `${vt.name} (You)`
                                                            : vt.name })] })] }, vt.trackId));
                                }
                                return (_jsxs(AudioTrackCard, { "$cardWidth": audioCardWidth, children: [track.isLocal && (_jsx(RemoveTrackButton, { "data-remove-btn": true, onClick: (e) => {
                                                e.stopPropagation();
                                                StreamrAppService.getNotificationInterface().notifyRemoveLocalTrack(track.trackId);
                                            }, children: "\u2715" })), _jsx(VerticalSliderArea, { "$cardHeight": cardHeight, "$muted": track.muted, children: _jsx(VerticalSlider, { value: track.gain, onChange: (val) => {
                                                    StreamrAppService.getNotificationInterface().notifySetAudioTrackGain(track.trackId, val);
                                                } }) }), _jsx(AudioMuteButton, { "$muted": track.muted, onClick: () => {
                                                StreamrAppService.getNotificationInterface().notifySetAudioTrackMuted(track.trackId, !track.muted);
                                            }, children: track.muted ? (_jsx(MutedSpeakerIcon, { size: "x1" })) : (_jsx(UnmutedSpeakerIcon, { size: "x1" })) })] }, track.trackId));
                            })] }, group.userId))), uploadedImages.map((image) => (_jsxs(VideoTrackCard, { "$highlighted": image.id === highlightedSourceId, "$cardWidth": cardWidth, onClick: () => onParticipantClick?.({
                            id: image.id,
                            name: image.name,
                        }), children: [_jsx(ThumbnailArea, { "$highlighted": image.id === highlightedSourceId, "$cardHeight": cardHeight, children: _jsx(ImagePreview, { src: image.objectUrl, alt: image.name }) }), _jsxs(NameRow, { children: [_jsx(ImageIconSmall, {}), _jsx(ParticipantName, { children: image.name })] })] }, image.id))), ografGraphics.map((graphic) => (_jsx(OgrafTrackCard, { graphic: graphic, cardWidth: cardWidth, cardHeight: cardHeight, highlighted: `ograf:${graphic.id}` === highlightedSourceId, onClick: () => onParticipantClick?.({
                            id: `ograf:${graphic.id}`,
                            name: graphic.name,
                        }) }, graphic.id))), _jsxs(InviteCard, { ref: inviteCardRef, onClick: () => setShowInvitePopup((v) => !v), children: [_jsx(InviteIconArea, { "$cardHeight": cardHeight, children: _jsx(PlusIcon, {}) }), _jsx(InviteLabel, { children: "Add track(s)" })] })] }), canScrollRight && (_jsx(ArrowButton, { "$side": "right", onClick: () => scrollBy(1), children: _jsx(ChevronRightIcon, {}) })), showInvitePopup &&
                popupPos &&
                createPortal(_jsx(PortalPopupAnchor, { ref: popupRef, style: {
                        left: popupPos.left,
                        top: popupPos.top,
                    }, children: _jsx(InvitePopup, { onClose: () => setShowInvitePopup(false), onUploadImage: handleUploadImage, onShareScreen: handleShareScreen, onAddCamera: handleAddCamera, onAddMicrophone: handleAddMicrophone, onAddRemoteCamera: handleAddRemoteCamera, onAddOgrafGraphic: handleAddOgrafGraphic }) }), document.body)] }));
}
function ImageIconSmall() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": "Image", children: [_jsx("rect", { x: "2", y: "4", width: "16", height: "12", rx: "2", stroke: "currentColor", strokeWidth: "1.5" }), _jsx("circle", { cx: "7", cy: "8", r: "1.5", fill: "currentColor" }), _jsx("path", { d: "M2 13l4.5-4 3.5 3 2.5-2L18 14", stroke: "currentColor", strokeWidth: "1.5", strokeLinejoin: "round" })] }));
}
function PersonIconSvg() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": "Person", children: [_jsx("circle", { cx: "7", cy: "4", r: "2.5", fill: "currentColor" }), _jsx("path", { d: "M2.5 12C2.5 9.51 4.51 7.5 7 7.5C9.49 7.5 11.5 9.51 11.5 12", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", fill: "none" })] }));
}
function ChevronLeftIcon() {
    return (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("title", { children: "Scroll left" }), _jsx("path", { d: "M12.5 15L7.5 10L12.5 5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
function ChevronRightIcon() {
    return (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("title", { children: "Scroll right" }), _jsx("path", { d: "M7.5 15L12.5 10L7.5 5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
const FilmStripRoot = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: hidden;
    gap: 0.25rem;
`;
const FilmStripScroll = styled.div `
    display: inline-flex;
    flex-direction: row;
    gap: ${CARD_GAP}px;
    overflow-x: auto;
    max-width: 100%;
    padding: 0.25rem 0;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;
const ArrowButton = styled.button `
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(57, 56, 56, 0.6);
    backdrop-filter: blur(10px);
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition:
        background 150ms ease,
        color 150ms ease;

    &:hover {
        background: rgba(57, 56, 56, 0.9);
        color: white;
    }
`;
const GroupContainer = styled.div `
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${CARD_GAP}px;
`;
const ParticipantSeparator = styled.div `
    flex-shrink: 0;
    width: 0;
    height: ${({ $cardHeight }) => $cardHeight}px;
    border-left: 2px dashed rgba(255, 255, 255, 0.3);
    margin: 0 4px;
`;
const VideoTrackCard = styled.div `
    flex-shrink: 0;
    width: ${({ $cardWidth }) => $cardWidth}px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    transition: transform 100ms ease;
    position: relative;

    &:hover {
        transform: scale(1.02);
    }

    &:hover > [data-remove-btn] {
        opacity: 1;
    }
`;
const RemoveTrackButton = styled.button `
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 150ms ease;
    z-index: 5;
    padding: 0;

    &:hover {
        background: rgba(220, 50, 50, 0.8);
    }
`;
const AudioTrackCard = styled.div `
    flex-shrink: 0;
    width: ${({ $cardWidth }) => $cardWidth}px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    &:hover > [data-remove-btn] {
        opacity: 1;
    }
`;
const VerticalSliderArea = styled.div `
    width: 100%;
    height: ${({ $cardHeight }) => $cardHeight}px;
    border-radius: 8px;
    background: ${({ $muted }) => $muted ? 'rgba(222, 53, 11, 0.10)' : 'rgba(57, 56, 56, 0.6)'};
    border: 2px solid rgba(255, 255, 255, 0.12);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 0;
`;
const VSliderTrack = styled.div `
    position: relative;
    width: 6px;
    height: 100%;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
    cursor: pointer;
    touch-action: none;
`;
const VSliderFill = styled.div `
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.6);
    pointer-events: none;
`;
const VSliderThumb = styled.div `
    position: absolute;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: white;
    transform: translate(-50%, 50%);
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
`;
const AudioMuteButton = styled.div `
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 0;
    cursor: pointer;
    color: ${({ $muted }) => $muted ? 'rgba(222, 53, 11, 0.8)' : 'rgba(255, 255, 255, 0.7)'};
    transition: color 150ms ease;

    &:hover {
        color: ${({ $muted }) => $muted ? 'rgba(222, 53, 11, 1)' : 'rgba(255, 255, 255, 0.9)'};
    }
`;
const ThumbnailArea = styled.div `
    position: relative;
    width: 100%;
    height: ${({ $cardHeight }) => $cardHeight}px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid
        ${({ $highlighted }) => $highlighted
    ? 'var(--tv-ui-color-13, #7B61FF)'
    : 'rgba(255, 255, 255, 0.12)'};
    box-sizing: border-box;
    transition: border-color 150ms ease;
`;
const ThumbnailContainer = styled.div `
    width: 100%;
    height: 100%;
    position: relative;
    background: #000;
`;
const ThumbnailCanvasEl = styled.canvas `
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;
const NameRow = styled.div `
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.375rem;
    color: rgba(255, 255, 255, 0.85);
`;
const ParticipantName = styled.span `
    font-size: 0.8125rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.25rem;
`;
const InviteCard = styled.div `
    flex-shrink: 0;
    width: 140px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    transition: transform 100ms ease;

    &:hover {
        transform: scale(1.02);
    }
`;
const InviteIconArea = styled.div `
    width: 100%;
    height: ${({ $cardHeight }) => $cardHeight}px;
    border-radius: 8px;
    background: rgba(57, 56, 56, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
    box-sizing: border-box;

    svg {
        width: 24px;
        height: 24px;
    }

    ${InviteCard}:hover & {
        background: rgba(57, 56, 56, 0.8);
        color: rgba(255, 255, 255, 0.8);
    }
`;
const InviteLabel = styled.div `
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.6);
    padding: 0.25rem 0.375rem;
    white-space: nowrap;
    line-height: 1.25rem;
`;
const ImagePreview = styled.img `
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;
const HiddenFileInput = styled.input `
    display: none;
`;
const PortalPopupAnchor = styled.div `
    position: fixed;
    z-index: 10000;
    transform: translate(-50%, -100%);
    padding-bottom: 8px;
`;
